import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  COMMUNITY_TEMPLATE_BATCH_LIKE_STATUS_KEY,
  COMMUNITY_TEMPLATE_LIKE_STATUS_KEY,
  type LikeResponse,
} from '../../contracts';
import { likeTemplate, unlikeTemplate } from '../api/templates';

interface UseMutateCommunityTemplateLikeProps {
  templateId: string;
  onSuccess?: (data: LikeResponse) => void;
  /**
   * Analytics as a PORT, fired where the dispatch used to fire (ARCFE-921).
   * The branch stays here because it reads `data.liked` — the decision is data,
   * only the emission is an app reaction.
   */
  onLikeChanged?: (liked: boolean, templateId: string) => void;
}

export function useMutateCommunityTemplateLike({
  templateId,
  onSuccess,
  onLikeChanged,
}: UseMutateCommunityTemplateLikeProps) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['likeTemplate', templateId],
    mutationFn: async ({ liked }: { liked: boolean }) => {
      const response = liked
        ? await unlikeTemplate(templateId)
        : await likeTemplate(templateId);
      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate the like status queries
      void queryClient.invalidateQueries({
        queryKey: [COMMUNITY_TEMPLATE_LIKE_STATUS_KEY, templateId],
      });
      void queryClient.invalidateQueries({
        queryKey: [COMMUNITY_TEMPLATE_BATCH_LIKE_STATUS_KEY],
      });

      onLikeChanged?.(data.liked, templateId);

      onSuccess?.(data);
    },
  });
}
